import { Link, useMatchRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Menu } from '@/assets/icons';
import { FeedbackMenuItem } from '@/components/feedback-menu-item';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsOfflineApp } from '@/contexts/offline-mode-context';
import { cn } from '@/lib/utils';
import { RELEASE_NOTES, type ReleaseNotesEntry } from '@/release-notes/manifest';
import { useWhatsNewUnread } from '@/release-notes/seen-store';
import { UnreadDot } from '@/release-notes/unread-dot';
import { WhatsNewDialog } from '@/release-notes/whats-new-dialog';
import { WhatsNewMenuItem } from '@/release-notes/whats-new-menu-item';
import { helpUrlForLanguage } from '@/utils/help-url';

// `reloadDocument` forces a full page load when entering an editor route. Each
// editor mounts its own Pixi Application (its own WebGPU device / WebGL context);
// SPA-switching between editors leaves the old GPU resource un-reclaimed until GC,
// and the accumulation eventually crashes the canvas ("WebGL context lost" /
// GpuBatchAdaptor "gpuProgram" undefined). A hard load tears down the whole
// document so the browser reclaims the GPU resource deterministically.
const LINKS = [
    { to: '/', labelKey: 'nav.home', reloadDocument: false },
    { to: '/grid-editor', labelKey: 'nav.knit', reloadDocument: true },
    { to: '/crochet', labelKey: 'nav.crochet', reloadDocument: true },
] as const;

interface AppNavProps {
    /** Injectable for tests; defaults to the bundled release-notes manifest. */
    releaseNotes?: readonly ReleaseNotesEntry[];
}

export function AppNav({ releaseNotes = RELEASE_NOTES }: AppNavProps = {}) {
    const { t, i18n } = useTranslation();
    const matchRoute = useMatchRoute();
    // The offline build has no Clerk UserButton, which is otherwise the only
    // way into Settings, so the menu carries a Settings entry there. It also
    // has no network: What's new (release-note markdown, /whats-new) and
    // Feedback (Userback's remote script) are hidden.
    const offline = useIsOfflineApp();
    // Controlled so the feedback item can hold the menu open while its widget
    // loads and dismiss it itself once the form is up.
    const [open, setOpen] = useState(false);
    const [whatsNewOpen, setWhatsNewOpen] = useState(false);
    const unread = useWhatsNewUnread(releaseNotes) && !offline;

    return (
        <>
            <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        size="icon"
                        aria-label={unread ? t('whatsNew.menuLabelUnread') : t('nav.menuLabel')}
                        className="relative"
                    >
                        <Menu className="size-4" />
                        {unread && <UnreadDot className="absolute top-1 right-1" />}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-44" align="start" sideOffset={4}>
                    {LINKS.map(({ to, labelKey, reloadDocument }) => {
                        const isActive = !!matchRoute({ to });
                        return (
                            <DropdownMenuItem
                                key={to}
                                asChild
                                className={cn(
                                    isActive &&
                                        'bg-setoff text-setoff-foreground font-semibold focus:bg-setoff focus:text-setoff-foreground'
                                )}
                            >
                                <Link to={to} reloadDocument={reloadDocument}>
                                    {t(labelKey)}
                                </Link>
                            </DropdownMenuItem>
                        );
                    })}
                    {offline && (
                        <DropdownMenuItem
                            asChild
                            className={cn(
                                !!matchRoute({ to: '/settings' }) &&
                                    'bg-setoff text-setoff-foreground font-semibold focus:bg-setoff focus:text-setoff-foreground'
                            )}
                        >
                            <Link to="/settings" search={{ section: 'appearance' }}>
                                {t('settingsPage.title')}
                            </Link>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                        <a
                            href={helpUrlForLanguage(i18n.language)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('nav.help')}
                        </a>
                    </DropdownMenuItem>
                    {!offline && releaseNotes.length > 0 && (
                        <WhatsNewMenuItem unread={unread} onSelect={() => setWhatsNewOpen(true)} />
                    )}
                    {!offline && <FeedbackMenuItem onOpened={() => setOpen(false)} />}
                </DropdownMenuContent>
            </DropdownMenu>
            {!offline && (
                <WhatsNewDialog
                    open={whatsNewOpen}
                    onOpenChange={setWhatsNewOpen}
                    surface="menu"
                    entries={releaseNotes}
                />
            )}
        </>
    );
}
